import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, type Category, type Product } from "@/lib/api";

type CatalogView = "grid" | "list";
type CatalogSort = "default" | "name" | "price_asc" | "price_desc";

const formatMoney = (value: string | number | null | undefined) => Number(value || 0).toLocaleString("ru-RU");

function getProductMinPrice(product: Product) {
  const prices = product.skus.map((sku) => Number(sku.current_price)).filter((price) => Number.isFinite(price));
  return prices.length > 0 ? Math.min(...prices) : 0;
}

function ProductStatusBadge({ status }: { status: Product["status"] }) {
  const tone =
    status === "active"
      ? "border-ok-border bg-ok-bg text-ok"
      : status === "hidden"
        ? "border-neutral-border bg-neutral-bg text-neutral"
        : "border-warn-border bg-warn-bg text-warn";

  const label = status === "active" ? "Активен" : status === "hidden" ? "Скрыт" : "Черновик";
  return <span className={`status-badge ${tone}`}>{label}</span>;
}

function ProductMarketingBadges({ product }: { product: Product }) {
  const badges = [
    product.is_bestseller ? "Хит продаж" : null,
    product.is_wholesale ? "Оптом дешевле" : null,
  ].filter(Boolean);

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((badge) => (
        <span key={badge} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
          {badge}
        </span>
      ))}
    </div>
  );
}

export default function CatalogPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [sort, setSort] = useState<CatalogSort>("default");
  const [view, setView] = useState<CatalogView>("grid");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    Promise.all([api.categories(), api.products()])
      .then(([nextCategories, nextProducts]) => {
        if (!mounted) return;
        setCategories(nextCategories);
        setProducts(nextProducts);
        setError("");
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : "Не удалось загрузить каталог");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const rootCategories = useMemo(
    () => categories.filter((cat) => !cat.parent_id && cat.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [categories]
  );

  const categoryNameById = useMemo(() => new Map(categories.map((cat) => [cat.id, cat.name])), [categories]);
  const childCategoryIdsByRootId = useMemo(() => {
    const result = new Map<string, Set<string>>();
    for (const root of rootCategories) {
      result.set(root.id, new Set(categories.filter((cat) => cat.parent_id === root.id).map((cat) => cat.id)));
    }
    return result;
  }, [categories, rootCategories]);
  const productMatchesCategory = useCallback(
    (product: Product, categoryId: string) => {
      const acceptedCategoryIds = new Set([categoryId, ...(childCategoryIdsByRootId.get(categoryId) || [])]);
      return (
        product.category_id === categoryId ||
        (product.subcategory_id ? acceptedCategoryIds.has(product.subcategory_id) : false) ||
        product.filter_category_ids.some((filterCategoryId) => acceptedCategoryIds.has(filterCategoryId))
      );
    },
    [childCategoryIdsByRootId]
  );

  const stats = useMemo(
    () => ({
      total: products.length,
      active: products.filter((product) => product.status === "active").length,
      draft: products.filter((product) => product.status === "draft").length,
      hidden: products.filter((product) => product.status === "hidden").length,
    }),
    [products]
  );

  const filteredProducts = useMemo(() => {
    const byCategory = selectedCategoryId ? products.filter((product) => productMatchesCategory(product, selectedCategoryId)) : products;

    const next = [...byCategory];
    if (sort === "name") {
      next.sort((a, b) => a.name.localeCompare(b.name, "ru"));
    }
    if (sort === "price_asc") {
      next.sort((a, b) => getProductMinPrice(a) - getProductMinPrice(b));
    }
    if (sort === "price_desc") {
      next.sort((a, b) => getProductMinPrice(b) - getProductMinPrice(a));
    }
    return next;
  }, [productMatchesCategory, products, selectedCategoryId, sort]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-[-0.03em] text-ink">Каталог</h2>
          <p className="mt-2 text-sm text-ink-4">
            {stats.total} товаров · <span className="font-semibold text-ok">{stats.active} активных</span> ·{" "}
            <span className="font-semibold text-warn">{stats.draft} черновиков</span> · {stats.hidden} скрытых
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn-secondary">
            <i className="ri-download-2-line" />
            Экспорт
          </button>
          <button type="button" onClick={() => navigate("/catalog/new")} className="btn-primary">
            <i className="ri-add-line" />
            Новый товар
          </button>
        </div>
      </div>

      <div className="panel p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
          <div className="flex flex-1 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategoryId("")}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                !selectedCategoryId ? "border-amber-700 bg-amber text-white" : "border-border bg-panel text-ink-3 hover:border-ink-mute hover:text-ink"
              }`}
            >
              Все
            </button>
            {rootCategories.map((category) => {
              const active = selectedCategoryId === category.id;
              const count = products.filter((product) => productMatchesCategory(product, category.id)).length;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedCategoryId(active ? "" : category.id)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                    active ? "border-amber-700 bg-amber text-white" : "border-border bg-panel text-ink-3 hover:border-ink-mute hover:text-ink"
                  }`}
                >
                  {category.name}
                  <span className="ml-1 opacity-70">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <select value={sort} onChange={(event) => setSort(event.target.value as CatalogSort)} className="soft-select min-w-[170px]">
              <option value="default">По умолчанию</option>
              <option value="name">По названию</option>
              <option value="price_asc">Цена ↑</option>
              <option value="price_desc">Цена ↓</option>
            </select>

            <div className="flex items-center rounded-xl border border-border bg-panel p-1 shadow-ds-1">
              <button
                type="button"
                onClick={() => setView("grid")}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition ${view === "grid" ? "bg-panel-2 text-ink" : "text-ink-4 hover:text-ink"}`}
                aria-label="Сетка"
              >
                <i className="ri-grid-line" />
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition ${view === "list" ? "bg-panel-2 text-ink" : "text-ink-4 hover:text-ink"}`}
                aria-label="Список"
              >
                <i className="ri-list-check" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-danger-border bg-danger-bg px-4 py-3 text-sm text-danger">{error}</div> : null}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="panel overflow-hidden">
              <div className="skel aspect-[4/3] w-full rounded-none" />
              <div className="space-y-2 p-3">
                <div className="skel h-3 w-20" />
                <div className="skel h-4 w-full" />
                <div className="skel h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="panel px-6 py-14 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-panel-2 text-xl text-ink-4">
            <i className="ri-search-line" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink">Ничего не найдено</h3>
          <p className="mt-2 text-sm text-ink-4">Измените запрос или сбросьте категорию, чтобы увидеть больше товаров.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
          {filteredProducts.map((product) => (
            <button
              key={product.product_id}
              type="button"
              onClick={() => navigate(`/catalog/${product.product_id}`)}
              className="panel group overflow-hidden text-left transition hover:-translate-y-0.5 hover:shadow-ds-2"
            >
              <div className="relative aspect-[4/3] overflow-hidden border-b border-border-soft bg-panel-2">
                {product.images.length > 0 ? (
                  <img
                    src={product.images[0].public_url}
                    alt={product.name}
                    className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-2xl text-ink-mute">
                    <i className="ri-image-line" />
                  </div>
                )}
                <div className="absolute left-2 top-2 scale-90 origin-top-left">
                  <ProductStatusBadge status={product.status} />
                </div>
              </div>

              <div className="space-y-2 p-3">
                <p className="truncate text-[11px] font-medium text-ink-4">
                  {product.category_id ? categoryNameById.get(product.category_id) : "Без категории"}
                </p>
                <div>
                  <h3 className="line-clamp-2 text-xs font-semibold leading-5 text-ink">{product.name}</h3>
                  <p className="mt-0.5 line-clamp-1 text-[11px] leading-4 text-ink-4">
                    {product.content_block || product.description || "Описание пока не заполнено."}
                  </p>
                </div>
                <ProductMarketingBadges product={product} />
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-[10px] text-ink-mute">от</p>
                    <p className="text-sm font-bold text-amber-800">{formatMoney(getProductMinPrice(product))} ₽</p>
                  </div>
                  <p className="text-right text-[11px] text-ink-4">{product.skus.length} SKU</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="divide-y divide-border-soft">
            {filteredProducts.map((product) => (
              <button
                key={product.product_id}
                type="button"
                onClick={() => navigate(`/catalog/${product.product_id}`)}
                className="grid w-full gap-3 px-4 py-3 text-left transition hover:bg-panel-2 md:grid-cols-[56px_1fr_110px_80px_20px]"
              >
                <div className="h-14 overflow-hidden rounded-lg bg-panel-2">
                  {product.images.length > 0 ? (
                    <img src={product.images[0].public_url} alt={product.name} className="h-full w-full object-cover object-top" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xl text-ink-mute">
                      <i className="ri-image-line" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-ink-4">
                    {product.category_id ? categoryNameById.get(product.category_id) : "Без категории"}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-ink">{product.name}</p>
                  <p className="mt-0.5 text-xs text-ink-4">{product.skus.length} SKU · {product.status}</p>
                  <div className="mt-2">
                    <ProductMarketingBadges product={product} />
                  </div>
                </div>
                <div className="flex items-center">
                  <ProductStatusBadge status={product.status} />
                </div>
                <div className="flex items-center text-sm font-semibold text-amber-800">{formatMoney(getProductMinPrice(product))} ₽</div>
                <div className="flex items-center justify-end text-ink-mute">
                  <i className="ri-arrow-right-s-line text-lg" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
